import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Badge, Button, Input, Table } from 'reactstrap';

import * as userUrls from '../../urls/api-users/admin';

const panelStyles = {
  container: {
    background: 'rgba(39, 41, 61, 0.45)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '12px',
    padding: '16px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  headerActions: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  title: {
    margin: 0,
  },
  copy: {
    color: 'rgba(255, 255, 255, 0.72)',
    marginBottom: '16px',
  },
  assignGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(240px, 1fr) 140px',
    gap: '10px',
    alignItems: 'end',
    marginBottom: '18px',
  },
  status: {
    marginBottom: '12px',
    fontSize: '0.9rem',
  },
  modeNotice: {
    marginBottom: '12px',
    padding: '10px 12px',
    borderRadius: '10px',
    background: 'rgba(43, 187, 174, 0.14)',
    border: '1px solid rgba(43, 187, 174, 0.32)',
    color: '#d7fffb',
    fontSize: '0.9rem',
  },
  meta: {
    color: 'rgba(255,255,255,0.68)',
    fontSize: '0.85rem',
    marginTop: '4px',
  },
  tableWrap: {
    overflowX: 'auto',
  },
  empty: {
    color: 'rgba(255,255,255,0.68)',
    textAlign: 'center',
    padding: '16px 0',
  },
  inlineActions: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
};

const normalizeUiPartnerType = partnerType => partnerType === 'affiliate_partner' ? 'affiliate_partner' : 'referral_partner';
const formatPartnerTypeLabel = partnerType => normalizeUiPartnerType(partnerType) === 'affiliate_partner' ? 'Affiliate' : 'Referral Partner';

const DriverBusinessAssignmentsPanel = ({ driverAuth0Id }) => {
  const [businesses, setBusinesses] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingBusinesses, setLoadingBusinesses] = useState(false);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCandidateList, setShowCandidateList] = useState(false);

  const authHeaders = {
    Authorization: 'Bearer ' + localStorage.getItem('access_token'),
    'content-type': 'application/json',
  };

  const fetchBusinesses = () => {
    if (!driverAuth0Id) {
      setBusinesses([]);
      return Promise.resolve();
    }

    setLoadingBusinesses(true);
    return axios({
      method: 'get',
      url: userUrls.referralPartnerBusinesses(driverAuth0Id),
      headers: authHeaders,
    })
      .then(response => {
        const payload = response && response.data && response.data.payload ? response.data.payload : {};
        setBusinesses(Array.isArray(payload.results) ? payload.results : []);
      })
      .catch(fetchError => {
        const nextMessage = fetchError && fetchError.response && fetchError.response.data
          ? (fetchError.response.data.message || fetchError.response.data.debug)
          : 'Unable to load business assignments.';
        setError(nextMessage || 'Unable to load business assignments.');
      })
      .finally(() => {
        setLoadingBusinesses(false);
      });
  };

  const fetchCandidates = nextSearchQuery => {
    if (!driverAuth0Id) {
      setCandidates([]);
      return Promise.resolve();
    }

    setLoadingCandidates(true);
    return axios({
      method: 'get',
      url: userUrls.referralPartnerBusinessCandidates(driverAuth0Id),
      headers: authHeaders,
      params: {
        search: nextSearchQuery || undefined,
      },
    })
      .then(response => {
        const payload = response && response.data && response.data.payload ? response.data.payload : {};
        const results = Array.isArray(payload.results) ? payload.results : [];
        setCandidates(results);
      })
      .catch(fetchError => {
        const nextMessage = fetchError && fetchError.response && fetchError.response.data
          ? (fetchError.response.data.message || fetchError.response.data.debug)
          : 'Unable to load business search results.';
        setError(nextMessage || 'Unable to load business search results.');
      })
      .finally(() => {
        setLoadingCandidates(false);
      });
  };

  useEffect(() => {
    setError('');
    setSuccess('');
    setShowCandidateList(false);
    fetchBusinesses();
  }, [driverAuth0Id]);

  useEffect(() => {
    if (!driverAuth0Id) {
      setCandidates([]);
      return;
    }

    const trimmedSearchQuery = searchQuery.trim();

    if (trimmedSearchQuery === '' && !showCandidateList) {
      setCandidates([]);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      fetchCandidates(trimmedSearchQuery);
    }, 250);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [driverAuth0Id, searchQuery, showCandidateList]);

  const handleAssign = (candidate, partnerType) => {
    const merchantUserId = candidate && candidate.merchant_user_id ? candidate.merchant_user_id : null;
    const merchantAuth0Id = candidate && candidate.merchant_auth0_id ? candidate.merchant_auth0_id : null;
    const merchantStoreId = candidate && candidate.merchant_store_id ? candidate.merchant_store_id : null;

    if (!merchantUserId && !merchantAuth0Id) {
      return;
    }

    setSavingAssignment(true);
    setError('');
    setSuccess('');

    axios({
      method: 'post',
      url: userUrls.referralPartnerBusinesses(driverAuth0Id),
      headers: authHeaders,
      data: {
        merchant_user_id: merchantUserId,
        merchant_auth0_id: merchantAuth0Id,
        merchant_store_id: merchantStoreId,
        partner_type: partnerType,
      },
    })
      .then(() => {
        setSuccess('Business assignment updated.');
        return Promise.all([fetchBusinesses(), fetchCandidates(searchQuery)]);
      })
      .catch(saveError => {
        const nextMessage = saveError && saveError.response && saveError.response.data
          ? (saveError.response.data.message || saveError.response.data.debug)
          : 'Unable to update business assignment.';
        setError(nextMessage || 'Unable to update business assignment.');
      })
      .finally(() => {
        setSavingAssignment(false);
      });
  };

  const handleUnassign = merchantReferralId => {
    if (!merchantReferralId) {
      return;
    }

    setSavingAssignment(true);
    setError('');
    setSuccess('');

    axios({
      method: 'delete',
      url: userUrls.referralPartnerBusinessAssignment(driverAuth0Id, merchantReferralId),
      headers: authHeaders,
    })
      .then(() => {
        setSuccess('Business unassigned.');
        return Promise.all([fetchBusinesses(), fetchCandidates(searchQuery)]);
      })
      .catch(deleteError => {
        const nextMessage = deleteError && deleteError.response && deleteError.response.data
          ? (deleteError.response.data.message || deleteError.response.data.debug)
          : 'Unable to remove business assignment.';
        setError(nextMessage || 'Unable to remove business assignment.');
      })
      .finally(() => {
        setSavingAssignment(false);
      });
  };
  const isSearching = searchQuery.trim() !== '' || showCandidateList;
  const activeRows = isSearching ? candidates : businesses;

  return (
    <div style={panelStyles.container}>
      <div style={panelStyles.header}>
        <h2 style={panelStyles.title}>{showCandidateList ? 'Add Business' : 'Businesses'}</h2>
        <div style={panelStyles.headerActions}>
          <Button
            className={showCandidateList ? 'btn btn-warning' : 'btn btn-success'}
            onClick={() => {
              const nextShowCandidateList = !showCandidateList;
              setShowCandidateList(nextShowCandidateList);

              if (nextShowCandidateList) {
                fetchCandidates(searchQuery.trim());
              } else {
                setSearchQuery('');
              }
            }}
            disabled={loadingCandidates || savingAssignment}
          >
            {showCandidateList ? 'Done Adding' : 'Add Business'}
          </Button>
          <Button className="btn btn-default" onClick={() => fetchBusinesses()} disabled={loadingBusinesses || savingAssignment}>
            Refresh
          </Button>
        </div>
      </div>
      <p style={panelStyles.copy}>
        Review every merchant business this driver receives commission from. To add a business, search for it below and use the action buttons in that same table row.
      </p>

      {error ? <div style={{ ...panelStyles.status, color: '#ff6b6b' }}>{error}</div> : null}
      {success ? <div style={{ ...panelStyles.status, color: '#2ecc71' }}>{success}</div> : null}
      {showCandidateList ? (
        <div style={panelStyles.modeNotice}>
          Add business mode is on. Search for a merchant below, then use `Assign Referral` or `Assign Affiliate` in the matching row.
        </div>
      ) : null}

      <div style={panelStyles.assignGrid}>
        <div>
          <label>Search businesses</label>
          <Input
            value={searchQuery}
            onChange={event => setSearchQuery(event.target.value)}
            placeholder="Search merchant name or email"
          />
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            className="btn btn-simple"
            onClick={() => {
              setShowCandidateList(false);
              setSearchQuery('');
            }}
            disabled={savingAssignment || (!showCandidateList && searchQuery.trim() === '')}
          >
            Clear Search
          </Button>
        </div>
      </div>

      <div style={panelStyles.tableWrap}>
        <Table responsive>
          <thead className="text-primary">
            <tr>
              <th>Business</th>
              <th>Contact</th>
              <th>Commission</th>
              <th>Referral Code</th>
              <th>Assigned</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {!loadingBusinesses && !loadingCandidates && activeRows.length === 0 ? (
              <tr>
                <td colSpan="6" style={panelStyles.empty}>
                  {showCandidateList && searchQuery.trim() === ''
                    ? 'Type a business name or email above to find a company to assign.'
                    : isSearching
                    ? 'No matching businesses found.'
                    : 'No businesses are assigned to this driver yet.'}
                </td>
              </tr>
            ) : null}
            {isSearching
              ? activeRows.map(candidate => {
                  const existingAssignmentLabel = candidate.existing_partner_type
                    ? formatPartnerTypeLabel(candidate.existing_partner_type)
                    : 'Not assigned';
                  const candidateKey = candidate.merchant_user_id || candidate.merchant_auth0_id || candidate.merchant_store_id || candidate.merchant_email || candidate.merchant_name;

                  return (
                    <tr key={candidateKey}>
                      <td>
                        <div>{candidate.merchant_name || 'Unknown business'}</div>
                        {candidate.merchant_store_id ? <div style={panelStyles.meta}>Store ID: {candidate.merchant_store_id}</div> : null}
                      </td>
                      <td>
                        <div>{candidate.merchant_email || 'N/A'}</div>
                        {candidate.merchant_phone ? <div style={panelStyles.meta}>{candidate.merchant_phone}</div> : null}
                      </td>
                      <td>
                        {candidate.existing_partner_type ? (
                          <Badge color={normalizeUiPartnerType(candidate.existing_partner_type) === 'affiliate_partner' ? 'warning' : 'info'}>
                            {existingAssignmentLabel}
                          </Badge>
                        ) : (
                          <span style={panelStyles.meta}>Choose referral or affiliate</span>
                        )}
                      </td>
                      <td>N/A</td>
                      <td>{candidate.existing_merchant_referral_id ? 'Already assigned' : 'Not assigned'}</td>
                      <td>
                        <div style={panelStyles.inlineActions}>
                          <Button
                            className="btn btn-info btn-sm"
                            onClick={() => handleAssign(candidate, 'referral_partner')}
                            disabled={savingAssignment}
                          >
                            Assign Referral
                          </Button>
                          <Button
                            className="btn btn-warning btn-sm"
                            onClick={() => handleAssign(candidate, 'affiliate_partner')}
                            disabled={savingAssignment}
                          >
                            Assign Affiliate
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              : activeRows.map(business => (
                  <tr key={business.merchant_referral_id}>
                    <td>
                      <div>{business.merchant_name || 'Unknown business'}</div>
                      {business.merchant_store_id ? <div style={panelStyles.meta}>Store ID: {business.merchant_store_id}</div> : null}
                    </td>
                    <td>
                      <div>{business.merchant_email || 'N/A'}</div>
                      {business.merchant_phone ? <div style={panelStyles.meta}>{business.merchant_phone}</div> : null}
                    </td>
                    <td>
                      <Badge color={normalizeUiPartnerType(business.partner_type) === 'affiliate_partner' ? 'warning' : 'info'}>
                        {`${formatPartnerTypeLabel(business.partner_type)} - ${(Number(business.commission_rate || 0) * 100).toFixed(2)}%`}
                      </Badge>
                    </td>
                    <td>{business.referral_code || 'N/A'}</td>
                    <td>{business.created_at ? new Date(business.created_at).toLocaleDateString('en-US') : 'N/A'}</td>
                    <td>
                      <Button className="btn btn-danger btn-sm" onClick={() => handleUnassign(business.merchant_referral_id)} disabled={savingAssignment}>
                        Unassign
                      </Button>
                    </td>
                  </tr>
                ))}
            {loadingBusinesses || loadingCandidates ? (
              <tr>
                <td colSpan="6" style={panelStyles.empty}>{isSearching ? 'Searching businesses...' : 'Loading businesses...'}</td>
              </tr>
            ) : null}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default DriverBusinessAssignmentsPanel;